/**
 * Pinned from `/Users/saher/code/cafe/Tarabeeza-Backend` (branch main, read-only source),
 * ADR-0031:
 *   - src/modules/forecasting/forecasting.controller.ts
 *   - src/modules/forecasting/forecast-holidays.controller.ts + .service.ts
 *   - src/modules/forecasting/forecast-suggestions.controller.ts + .service.ts
 *   - src/modules/forecasting/forecast-health.controller.ts + .service.ts
 *   - src/modules/forecasting/forecast-points.service.ts + forecast-backtest.service.ts
 *   - src/modules/forecasting/dto/*.ts
 *   - src/shared/domain/forecasting.ts (ForecastedDay, BacktestResult)
 *   - prisma/schema.prisma (ForecastRun / ForecastPoint / ForecastBacktest /
 *     ForecastSuggestion / ForecastHoliday, and their enums)
 * One file, so a later change on that side is a one-file fix here.
 *
 * ADR-0031 in three sentences that shape every screen in this module:
 *   1. Every point carries `confidence`, `baselineValue` and a plain-English
 *      `assumptions` sentence — there is no model here to trust on faith.
 *   2. The backtest scores the chosen method against seasonal naive
 *      ("the honest one") on the SAME holdout, and `beatsBaseline` is
 *      reported even when it is false — which, on real seed data, it
 *      currently is.
 *   3. A suggestion never mutates anything. `INVENTORY_PAR` is applied by a
 *      manager through the EXISTING `POST /replenishment/pars`; `LABOUR_HEADCOUNT`
 *      has no apply step at all. Acknowledging one only records that a
 *      manager saw it.
 */

// ------------------------------------------------------------------ enums

export enum ForecastTarget {
  SALES = 'SALES',
  ITEM_DEMAND = 'ITEM_DEMAND',
  PREP_DEMAND = 'PREP_DEMAND',
  LABOUR_DEMAND = 'LABOUR_DEMAND',
  INVENTORY_REPLENISHMENT = 'INVENTORY_REPLENISHMENT',
  RESOURCE_UTILISATION = 'RESOURCE_UTILISATION',
}

/** The two targets with one daily series — the only ones a backtest can score directly. */
export const BACKTESTABLE_TARGETS: readonly ForecastTarget[] = [
  ForecastTarget.SALES,
  ForecastTarget.RESOURCE_UTILISATION,
];

/** Explainable statistics only — see `shared/domain/forecasting.ts`. No ML, no learned coefficient. */
export enum ForecastMethod {
  SEASONAL_NAIVE = 'SEASONAL_NAIVE',
  MOVING_AVERAGE = 'MOVING_AVERAGE',
  DAY_OF_WEEK_PROFILE = 'DAY_OF_WEEK_PROFILE',
  DAYPART_PROFILE = 'DAYPART_PROFILE',
}

export enum ForecastSuggestionKind {
  INVENTORY_PAR = 'INVENTORY_PAR',
  LABOUR_HEADCOUNT = 'LABOUR_HEADCOUNT',
}

export enum ForecastSuggestionStatus {
  PENDING = 'PENDING',
  ACKNOWLEDGED = 'ACKNOWLEDGED',
  DISMISSED = 'DISMISSED',
}

// -------------------------------------------------------------------- runs

/** `GET /forecasting/runs` row / `GET /forecasting/runs/:id` — one generation attempt. */
export interface ForecastRun {
  id: string;
  tenantId: string;
  branchId: string;
  target: ForecastTarget;
  method: ForecastMethod;
  startedAt: string;
  /** Null while running, and still null if the process died mid-run. */
  finishedAt: string | null;
  horizonStart: string;
  horizonEnd: string;
  historyStart: string | null;
  historyEnd: string | null;
  historyRowsUsed: number;
  pointsGenerated: number;
  suggestionsRaised: number;
  params: Record<string, unknown> | null;
  error: string | null;
  triggeredById: string | null;
  createdAt: string;
}

export interface TriggerForecastRunInput {
  branchId: string;
  targets: ForecastTarget[];
  /** Ignored for SALES, which always splits by daypart. */
  method?: ForecastMethod;
  /** LABOUR_DEMAND only. */
  ordersPerStaffPerDaypart?: number;
  /** INVENTORY_REPLENISHMENT only. */
  leadTimeDays?: number;
}

// ----------------------------------------------------------------- points

/**
 * `ForecastPoint` row. `predictedValue`/`lowerBound`/`upperBound`/`baselineValue`
 * are `Decimal(14,4)` on the wire — decimal STRINGS, never parsed client-side.
 * `confidence` is `Decimal(3,2)`, 0.00–1.00, derived from sample count alone —
 * never the method's own opinion of its fit.
 */
export interface ForecastPoint {
  id: string;
  tenantId: string;
  branchId: string;
  forecastRunId: string;
  target: ForecastTarget;
  date: string;
  /** MORNING / MIDDAY / AFTERNOON / EVENING / LATE_NIGHT, or null for the whole day. */
  daypart: string | null;
  subjectType: string | null;
  subjectId: string | null;
  predictedValue: string;
  lowerBound: string | null;
  upperBound: string | null;
  /** Always populated when computable — the honest "what if we'd just repeated last week" figure. */
  baselineValue: string | null;
  confidence: string;
  samples: number;
  /** A sentence, never a code — show this. */
  assumptions: string;
  createdAt: string;
}

/** `GET /forecasting/points` — "the current forecast": the latest completed run's points. */
export interface CurrentForecast {
  run: ForecastRun | null;
  points: ForecastPoint[];
}

// --------------------------------------------------------------- backtests

/**
 * `GET /forecasting/backtests` row / `GET /forecasting/backtests/:id`.
 * `mae`/`mape`/`rmse` and their `baseline*` twins are decimal strings, null
 * together when `insufficientData` is true. `beatsBaseline` is
 * `method.mae < baseline.mae` — reported even when false, which on the
 * shipped seed data it currently is (mae 1.8571 vs baselineMae 1.8571).
 */
export interface ForecastBacktest {
  id: string;
  tenantId: string;
  branchId: string;
  target: ForecastTarget;
  method: ForecastMethod;
  baselineMethod: ForecastMethod;
  historyStart: string;
  historyEnd: string;
  holdoutStart: string;
  holdoutEnd: string;
  sampleSize: number;
  insufficientData: boolean;
  mae: string | null;
  mape: string | null;
  rmse: string | null;
  baselineMae: string | null;
  baselineMape: string | null;
  baselineRmse: string | null;
  beatsBaseline: boolean | null;
  /** The measured verdict in words — reads the same from the API as from `psql`. */
  notes: string;
  ranById: string | null;
  ranAt: string;
}

export interface RunBacktestInput {
  branchId: string;
  target: ForecastTarget;
  /** Defaults to DAY_OF_WEEK_PROFILE. */
  method?: ForecastMethod;
  /** Days held out and scored. Default 14, min 7, max 60. */
  holdoutDays?: number;
}

// ------------------------------------------------------------- suggestions

export interface InventoryParSuggestionPayload {
  warehouseId: string;
  warehouseName?: string;
  inventoryItemId: string;
  inventoryItemName?: string;
  currentParLevel: string;
  suggestedParLevel: string;
  forecastedDailyConsumption: string;
  leadTimeDays?: number;
  safetyFactor?: string;
}

export interface LabourHeadcountSuggestionPayload {
  date: string;
  daypart: string;
  forecastedOrders: string;
  suggestedHeadcount: number;
  ordersPerStaffAssumption: number;
}

/** `GET /forecasting/suggestions` row / `GET /forecasting/suggestions/:id`. */
export interface ForecastSuggestion {
  id: string;
  tenantId: string;
  branchId: string;
  forecastRunId: string;
  kind: ForecastSuggestionKind;
  status: ForecastSuggestionStatus;
  payload: InventoryParSuggestionPayload | LabourHeadcountSuggestionPayload | Record<string, unknown>;
  /** The finding as one sentence, rendered at generation time. */
  reason: string;
  acknowledgedById: string | null;
  acknowledgedAt: string | null;
  dismissedById: string | null;
  dismissedAt: string | null;
  dismissReason: string | null;
  createdAt: string;
  branch?: { id: string; name: string };
}

export interface QueryForecastSuggestionsParams {
  page?: number;
  limit?: number;
  /** Defaults to PENDING server-side — the queue is what is waiting. */
  status?: ForecastSuggestionStatus;
  kind?: ForecastSuggestionKind;
  branchId?: string;
}

// --------------------------------------------------------------- holidays

/** `GET /forecasting/holidays` row — tenant-supplied, nothing inferred. */
export interface ForecastHoliday {
  id: string;
  tenantId: string;
  /** Null = every branch in the tenant observes it. */
  branchId: string | null;
  date: string;
  label: string;
  createdById: string | null;
  createdAt: string;
}

export interface CreateForecastHolidayInput {
  /** Omit for a tenant-wide holiday. */
  branchId?: string;
  date: string;
  label: string;
}

// ------------------------------------------------------------------ health

/**
 * `GET /forecasting/health` — verbatim `ForecastHealth`. Whether forecasting
 * is WORKING, not what it forecasts. `hasTradingHistory: false` is a real,
 * quiet, healthy state (a fresh tenant); it is reported distinctly from a run
 * that read zero rows on a branch that DOES have trading history, which is
 * the unbound-tenant-read signature and is reported as a problem.
 */
export interface ForecastHealth {
  healthy: boolean;
  hasTradingHistory: boolean;
  pendingSuggestions: number;
  lastRunStartedAt: string | null;
  lastSuccessfulRunAt: string | null;
  historyRowsUsedLastRun: number | null;
  pointsGeneratedLastRun: number | null;
  problems: string[];
}

// ------------------------------------------------------------------ errors

export function apiError(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(message)) return message.filter(Boolean).join(' · ');
  return (typeof message === 'string' && message.trim()) || fallback;
}
